import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PendingOperation {
  id: string;
  type: 'sale' | 'expense' | 'payment';
  data: any;
  timestamp: number;
  retryCount: number;
}

interface OnlineStatusState {
  isOnline: boolean;
  wasOffline: boolean;
  lastOnlineAt: Date | null;
  lastOfflineAt: Date | null;
  pendingOperations: PendingOperation[];
  connectionQuality: 'good' | 'slow' | 'poor' | 'unknown';
  latencyMs: number | null;
  isSyncing: boolean;
}

const STORAGE_KEY = 'vleplus_pending_operations';
const MAX_RETRY_COUNT = 3;

export function useOnlineStatus() {
  const [state, setState] = useState<OnlineStatusState>({
    isOnline: navigator.onLine,
    wasOffline: false,
    lastOnlineAt: navigator.onLine ? new Date() : null,
    lastOfflineAt: null,
    pendingOperations: [],
    connectionQuality: 'unknown',
    latencyMs: null,
    isSyncing: false,
  });

  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const syncInProgressRef = useRef(false);
  const pendingOperationsRef = useRef<PendingOperation[]>([]);

  // Keep ref in sync with state
  useEffect(() => {
    pendingOperationsRef.current = state.pendingOperations;
  }, [state.pendingOperations]);

  // Load pending operations from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const operations = JSON.parse(stored) as PendingOperation[];
        pendingOperationsRef.current = operations;
        setState(prev => ({ ...prev, pendingOperations: operations }));
        console.log('[OnlineStatus] Loaded', operations.length, 'pending operations from storage');
      }
    } catch (error) {
      console.error('[OnlineStatus] Error loading pending operations:', error);
    }
  }, []);

  // Save pending operations to localStorage
  const savePendingOperations = useCallback((operations: PendingOperation[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(operations));
    } catch (error) {
      console.error('[OnlineStatus] Error saving pending operations:', error);
    }
  }, []);

  // Add operation to queue
  const queueOperation = useCallback((type: PendingOperation['type'], data: any) => {
    const operation: PendingOperation = {
      id: `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      data,
      timestamp: Date.now(),
      retryCount: 0,
    };

    setState(prev => {
      const newOperations = [...prev.pendingOperations, operation];
      savePendingOperations(newOperations);
      console.log('[OnlineStatus] Queued operation:', operation.id);
      return { ...prev, pendingOperations: newOperations };
    });

    return operation.id;
  }, [savePendingOperations]);

  // Remove operation from queue
  const removeOperation = useCallback((operationId: string) => {
    setState(prev => {
      const newOperations = prev.pendingOperations.filter(op => op.id !== operationId);
      savePendingOperations(newOperations);
      console.log('[OnlineStatus] Removed operation:', operationId);
      return { ...prev, pendingOperations: newOperations };
    });
  }, [savePendingOperations]);

  // Update operation retry count
  const updateOperationRetry = useCallback((operationId: string) => {
    setState(prev => {
      const newOperations = prev.pendingOperations.map(op => 
        op.id === operationId ? { ...op, retryCount: op.retryCount + 1 } : op
      );
      savePendingOperations(newOperations);
      return { ...prev, pendingOperations: newOperations };
    });
  }, [savePendingOperations]);

  // Clear all pending operations
  const clearPendingOperations = useCallback(() => {
    setState(prev => ({ ...prev, pendingOperations: [] }));
    localStorage.removeItem(STORAGE_KEY);
    console.log('[OnlineStatus] Cleared all pending operations');
  }, []);

  // Execute a single pending operation
  const executeOperation = useCallback(async (operation: PendingOperation): Promise<boolean> => {
    console.log('[OnlineStatus] Executing operation:', operation.id, operation.type);
    
    try {
      switch (operation.type) {
        case 'sale': {
          const { items, ...saleData } = operation.data;
          const { data: sale, error: saleError } = await supabase
            .from('sales')
            .insert(saleData)
            .select()
            .single();
          
          if (saleError) throw saleError;
          
          if (items && items.length > 0 && sale) {
            const saleItems = items.map((item: any) => ({
              ...item,
              sale_id: sale.id,
            }));
            const { error: itemsError } = await supabase
              .from('sale_items')
              .insert(saleItems);
            if (itemsError) throw itemsError;
          }
          break;
        }
        case 'expense': {
          const { error } = await supabase
            .from('expenses')
            .insert(operation.data);
          if (error) throw error;
          break;
        }
        case 'payment': {
          const { saleId, amount, customerId, customerDueUpdate } = operation.data;
          
          // Update sale
          const { data: currentSale } = await supabase
            .from('sales')
            .select('received_amount, due_amount')
            .eq('id', saleId)
            .single();
          
          if (currentSale) {
            const newReceived = (currentSale.received_amount || 0) + amount;
            const newDue = Math.max(0, (currentSale.due_amount || 0) - amount);
            
            const { error } = await supabase
              .from('sales')
              .update({
                received_amount: newReceived,
                due_amount: newDue,
                payment_status: newDue === 0 ? 'paid' : 'partial',
              })
              .eq('id', saleId);
            
            if (error) throw error;
          }
          
          // Update customer if needed
          if (customerId && customerDueUpdate) {
            const { data: customer } = await supabase
              .from('customers')
              .select('total_due')
              .eq('id', customerId)
              .single();
            
            if (customer) {
              await supabase
                .from('customers')
                .update({ total_due: Math.max(0, (customer.total_due || 0) - amount) })
                .eq('id', customerId);
            }
          }
          break;
        }
        default:
          console.warn('[OnlineStatus] Unknown operation type:', operation.type);
          return false;
      }
      
      console.log('[OnlineStatus] Operation successful:', operation.id);
      return true;
    } catch (error) {
      console.error('[OnlineStatus] Operation failed:', operation.id, error);
      return false;
    }
  }, []);

  // Sync all pending operations - uses ref to avoid dependency on state.pendingOperations
  const syncPendingOperations = useCallback(async () => {
    if (syncInProgressRef.current) {
      console.log('[OnlineStatus] Sync already in progress, skipping');
      return;
    }
    
    // Use ref to get current operations without causing hook dependency issues
    const currentOperations = [...pendingOperationsRef.current];
    if (currentOperations.length === 0) {
      console.log('[OnlineStatus] No pending operations to sync');
      return;
    }
    
    syncInProgressRef.current = true;
    setState(prev => ({ ...prev, isSyncing: true }));
    console.log('[OnlineStatus] Starting sync of', currentOperations.length, 'operations');
    
    let successCount = 0;
    let failCount = 0;
    
    // Sort by timestamp to execute in order
    const sortedOperations = currentOperations.sort((a, b) => a.timestamp - b.timestamp);
    
    for (const operation of sortedOperations) {
      if (operation.retryCount >= MAX_RETRY_COUNT) {
        console.log('[OnlineStatus] Operation exceeded max retries, removing:', operation.id);
        removeOperation(operation.id);
        failCount++;
        continue;
      }
      
      const success = await executeOperation(operation);
      
      if (success) {
        removeOperation(operation.id);
        successCount++;
      } else {
        updateOperationRetry(operation.id);
        failCount++;
      }
    }
    
    syncInProgressRef.current = false;
    setState(prev => ({ ...prev, isSyncing: false }));
    
    if (successCount > 0) {
      toast.success(`Synced ${successCount} pending operation${successCount > 1 ? 's' : ''}`);
    }
    if (failCount > 0) {
      toast.error(`Failed to sync ${failCount} operation${failCount > 1 ? 's' : ''}`);
    }
    
    console.log('[OnlineStatus] Sync complete:', { successCount, failCount });
  }, [executeOperation, removeOperation, updateOperationRetry]);

  // Measure network latency to backend
  const measureLatency = useCallback(async (): Promise<number | null> => {
    if (!navigator.onLine) return null;
    
    const startTime = performance.now();
    try {
      // Use Supabase health check endpoint for accurate backend latency
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/`,
        {
          method: 'HEAD',
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          cache: 'no-store',
          signal: AbortSignal.timeout(10000),
        }
      );
      
      const latency = Math.round(performance.now() - startTime);
      console.log('[OnlineStatus] Backend latency:', latency, 'ms');
      return latency;
    } catch (error) {
      console.error('[OnlineStatus] Latency measurement failed:', error);
      return null;
    }
  }, []);

  // Run a full network speed test
  const runSpeedTest = useCallback(async (): Promise<{ latency: number | null; quality: 'good' | 'slow' | 'poor' | 'unknown' }> => {
    console.log('[OnlineStatus] Running speed test...');
    
    // Take 3 measurements and average them
    const measurements: number[] = [];
    
    for (let i = 0; i < 3; i++) {
      const latency = await measureLatency();
      if (latency !== null) {
        measurements.push(latency);
      }
      // Small delay between measurements
      if (i < 2) await new Promise(r => setTimeout(r, 300));
    }
    
    if (measurements.length === 0) {
      setState(prev => ({ ...prev, latencyMs: null, connectionQuality: 'poor' }));
      return { latency: null, quality: 'poor' };
    }
    
    const avgLatency = Math.round(measurements.reduce((a, b) => a + b, 0) / measurements.length);
    
    let quality: 'good' | 'slow' | 'poor' = 'good';
    if (avgLatency > 2000) quality = 'poor';
    else if (avgLatency > 500) quality = 'slow';
    
    setState(prev => ({ ...prev, latencyMs: avgLatency, connectionQuality: quality }));
    console.log('[OnlineStatus] Speed test complete:', avgLatency, 'ms,', quality);
    
    return { latency: avgLatency, quality };
  }, [measureLatency]);

  // Check connection quality with a lightweight ping
  const checkConnectionQuality = useCallback(async () => {
    if (!navigator.onLine) {
      setState(prev => ({ ...prev, connectionQuality: 'poor', latencyMs: null }));
      return;
    }

    const latency = await measureLatency();
    
    if (latency === null) {
      setState(prev => ({ ...prev, connectionQuality: 'poor', latencyMs: null }));
      return;
    }
    
    let quality: OnlineStatusState['connectionQuality'] = 'good';
    if (latency > 2000) quality = 'poor';
    else if (latency > 500) quality = 'slow';
    
    setState(prev => ({ ...prev, connectionQuality: quality, latencyMs: latency }));
  }, [measureLatency]);

  // Handle online event
  const handleOnline = useCallback(() => {
    console.log('[OnlineStatus] Network: ONLINE');
    setState(prev => ({
      ...prev,
      isOnline: true,
      wasOffline: true,
      lastOnlineAt: new Date(),
    }));
    checkConnectionQuality();
    
    // Auto-sync pending operations after a short delay
    setTimeout(() => {
      syncPendingOperations();
    }, 2000);
  }, [checkConnectionQuality, syncPendingOperations]);

  // Handle offline event
  const handleOffline = useCallback(() => {
    console.log('[OnlineStatus] Network: OFFLINE');
    setState(prev => ({
      ...prev,
      isOnline: false,
      lastOfflineAt: new Date(),
      connectionQuality: 'poor',
      latencyMs: null,
    }));
  }, []);

  // Set up event listeners - OPTIMIZED: Only ping when tab is active, 60s interval
  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial connection check
    checkConnectionQuality();

    // Visibility-aware periodic check - only ping when tab is active
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkConnectionQuality();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Reduced ping frequency: 60 seconds instead of 30
    pingIntervalRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') {
        checkConnectionQuality();
      }
    }, 60000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [handleOnline, handleOffline, checkConnectionQuality]);

  // Reset wasOffline flag after some time
  useEffect(() => {
    if (state.wasOffline && state.isOnline) {
      const timer = setTimeout(() => {
        setState(prev => ({ ...prev, wasOffline: false }));
      }, 10000); // Show "back online" message for 10 seconds

      return () => clearTimeout(timer);
    }
  }, [state.wasOffline, state.isOnline]);

  return {
    isOnline: state.isOnline,
    wasOffline: state.wasOffline,
    isOffline: !state.isOnline,
    lastOnlineAt: state.lastOnlineAt,
    lastOfflineAt: state.lastOfflineAt,
    pendingOperations: state.pendingOperations,
    pendingCount: state.pendingOperations.length,
    connectionQuality: state.connectionQuality,
    latencyMs: state.latencyMs,
    isSyncing: state.isSyncing,
    queueOperation,
    removeOperation,
    clearPendingOperations,
    syncPendingOperations,
    runSpeedTest,
  };
}
